import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store/index.js'
import Dashboard from '@/views/Dashboard.vue'
import startup from '@/api/modules/startup.js'
import { checkAccess } from '@/utils/checkAccess.js'
import OktaVue, { LoginCallback } from '@okta/okta-vue'
import { OktaAuth } from '@okta/okta-auth-js'

Vue.use(VueRouter)

let oktaAuth = new OktaAuth(startup.oidc)
Vue.use(OktaVue, { oktaAuth })

const routes = [
  {
    // handles OAuth callback
    path: '/login/callback',
    name: 'LoginCallback',
    component: LoginCallback,
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/access',
    name: 'Access',
    component: () => import(/* webpackChunkName: "Access" */ '@/views/User/Access.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/custom-groups',
    name: 'CustomGroups',
    component: () => import(/* webpackChunkName: "Groups" */ '@/views/CustomGroups/Groups.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/bill-history',
    name: 'BillHistory',
    component: () => import(/* webpackChunkName: "BillHistory" */ '@/views/BillHistory/BillHistory.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/progressive-purchasing/summary-and-nominations',
    name: 'SummaryAndNominations',
    component: () =>
      import(/* webpackChunkName: "SummaryAndNominations" */ '@/views/ProgressivePurchasing/SummaryAndNominations.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/progressive-purchasing/completed-nominations',
    name: 'CompletedNominations',
    component: () =>
      import(/* webpackChunkName: "CompletedNominations" */ '@/views/ProgressivePurchasing/CompletedNominations.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/progressive-purchasing/pending-nominations',
    name: 'PendingNominations',
    component: () =>
      import(/* webpackChunkName: "PendingNominations" */ '@/views/ProgressivePurchasing/PendingNominations.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/progressive-purchasing/nomination-request',
    name: 'NominationRequest',
    component: () =>
      import(/* webpackChunkName: "NominationRequest" */ '@/views/ProgressivePurchasing/NominationRequest.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/contact-us',
    name: 'ContactUs',
    component: () => import(/* webpackChunkName: "ContactUs" */ '@/views/ContactUs/ContactUs.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import(/* webpackChunkName: "ChangePassword" */ '@/views/ChangePassword.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import(/* webpackChunkName: "Reports" */ '@/views/Reports/Reports.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/reports/max-demand',
    name: 'MaxDemand',
    component: () => import(/* webpackChunkName: "Reports" */ '@/views/Reports/MaxDemand.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/reports/comparison',
    name: 'Comparison',
    component: () => import(/* webpackChunkName: "Reports" */ '@/views/Reports/Comparison.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/reports/interval-data',
    name: 'IntervalData',
    component: () => import(/* webpackChunkName: "Reports" */ '@/views/Reports/IntervalData/IntervalData.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/reports/interval-data-via-email',
    name: 'IntervalDataViaEmail',
    component: () => import(/* webpackChunkName: "Reports" */ '@/views/Reports/IntervalData/IntervalDataViaEmail.vue'),
    meta: {
      accessRestricted: true,
      requiresAuth: true,
    },
  },
  {
    path: '/accept-terms-conditions',
    name: 'AcceptTermsConditions',
    component: () => import(/* webpackChunkName: "AcceptTermsConditions" */ '@/views/AcceptTermsConditions.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/terms-conditions',
    name: 'TermsConditions',
    component: () => import(/* webpackChunkName: "TermsConditions" */ '@/views/TermsConditions.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/no-access',
    name: 'NoAccess',
    component: () => import(/* webpackChunkName: "NoAccess" */ '@/views/NoAccess.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/help-centre',
    name: 'HelpCentre',
    component: () => import(/* webpackChunkName: "HelpCentre" */ '@/views/HelpCentre.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/error',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "NotFound" */ '@/views/NotFound.vue'),
    alias: '*',
    meta: {
      requiresAuth: true,
    },
  },
]
console.log('Creating router on startup', startup.resourceServer.messagesUrl)
let router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

router.beforeEach((to, from, next) => {
  let redirectTo
  if (
    !store.state.auth.user.termsAccepted &&
    !['LoginCallback', 'AcceptTermsConditions', 'TermsConditions'].includes(to.name) &&
    store.state.auth.user.termsAccepted !== null
  ) {
    // If T&Cs have not been accepted by user then redirect to T&Cs route
    redirectTo = { name: 'AcceptTermsConditions' }
  } else if (
    to.meta?.accessRestricted &&
    store.state.accessToken != null &&
    !store.getters['auth/checkAccess'](to.name)
  ) {
    // If user isn't allowed to access the requested route then redirect
    redirectTo = { name: 'NoAccess' }
  }
  next(redirectTo)
})

export default router
